import React from "react";
import styled from "styled-components";
import I10n from "../../../features/i10n";
import MsxIcons from "../../msx-icons";
import { PRIMARY_COLOR, SECONDARY_COLOR } from "../../../color-constants";
import { get3DViewerAssemblyComposition, get3DViewerElementsNewAPI } from "./ducks";
import Checkbox from "../../../atoms/checkbox";
import { front_base_url } from "../../../constants";
import { API_INSTANCE_NO_CREDENTIALS } from "../../../utils/service-http";
import { log3DViewerExportConfiguration, log3DViewerExportErrorConfiguration } from "../../../utils/analytics-logger";

const Container = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  max-width: 1100px;
  min-width: 100%;
  width: 100%;
  max-width: 100%;
  min-height: 100%;
  height: 100%;
  max-height: 100%;
  box-sizing: content-box;
  background-color: #f4f4f4;
  color: #333333;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: ${SECONDARY_COLOR};
  background: var(--se-primary, ${SECONDARY_COLOR});
  color: white !important;
  color: var(--se-primary-contrast, white) !important;
  margin: 0px;
  padding: 15px 20px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-size: 24px;
  font-weight: 700;
`;

const CloseIcon = styled.span`
  display: inline-block;
  cursor: pointer;
`;

const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const ViewerWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 0px;
  .graphical-view-positioner {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

const ClusterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid black;
`;

const ClusterItem = styled.div`
  cursor: pointer;
  border-bottom: 1px solid black;
  padding: 10px;

  ${props => props.$disabled ? 'opacity: 0.5; pointer-events: none;' : ''}

  ${(props) =>
    props.$selected
      ? `
    color: ${SECONDARY_COLOR};
    color: var(--se-primary, ${SECONDARY_COLOR});
    `
      : ""}
`;

const ActionSectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 250px;
  overflow: auto;
  background-color: white;
  @media (max-width: 800px) {
    width: auto;
  }
`;

const ActionSection = styled.div`
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, .27);
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
`;

const ActionSectionHeader = styled.div`
  background-color: ${SECONDARY_COLOR};
  background-color: var(--se-primary, ${SECONDARY_COLOR});
  color: white !important;
  color: var(--se-primary-contrast, white) !important;
  padding: 10px;
`;

const HeaderTitle = styled.div`
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
`;

const CheckboxWrapper = styled.div`
  :not(:first-of-type) {
    margin-top: 10px;
  }
`;

const SelectedViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SelectedViewImage = styled.img`
`;

const SelectedViewSeparator = styled.hr`
  margin-top: 20px;
  margin-bottom: 20px;
  border: 0;
  border-top: 1px solid #cbcbcb;
  width: 100%;
`;

const PossibleViewsWrapper = styled.div`
  display: flex;
  justify-content: space-around;
`;

const PossibleView = styled.img = styled.img`
  cursor: pointer;
  ${props => props.$disabled ? 'pointer-events: none;' : ''}
`;

const Link = styled.span`
  color: ${PRIMARY_COLOR};
  margin-bottom: 10px;
  cursor: pointer;

  ${props => props.$disabled ? 'opacity: 0.5;pointer-events: none;' : ''}
`;

const LoaderContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: calc(50% - 32px);
  left: calc(50% - 32px);
  height: 70px;
  background: rgba(0, 0, 0, 0.5);

  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
  }
`;

const LoadingRing = styled.div`
  display: inline-block;
  position: relative;
  width: 64px;
  height: 64px;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 51px;
    height: 51px;
    margin: 6px;
    border-width: 6px;
    border-style: solid;
    border-image: initial;
    border-radius: 50%;
    animation: 1.2s cubic-bezier(0.5, 0, 0.5, 1) 0s infinite normal none running lds-ring;
    border-color: rgb(255, 255, 255) transparent transparent;
  }

  div:nth-child(1) {
    animation-delay: -0.45s;
  }

  div:nth-child(2) {
    animation-delay: -0.3s;
  }

  div:nth-child(3) {
    animation-delay: -0.15s;
}`;

const modelsResourcesEndpoint =
  process.env.REACT_APP_3D_VIEWER_RESOURCES_ENDPOINT;
const referencePointsEndpoint =
  process.env.REACT_APP_3D_VIEWER_REFERENCE_ENDPOINT;
const skeletonEndpoint =
  process.env.REACT_APP_3D_VIEWER_SKELETON_ENDPOINT;
const authBearerToken = process.env.REACT_APP_3D_VIEWER_AUTH_TOKEN;

const VIEWER_SCRIPT_ID = "modicon-plc-3d-viewer-script";

const DOWNLOAD_FORMATS = {
  collada: { extension: 'dae', mimeType: 'model/vnd.collada+xml' },
  dxf: { extension: 'dxf', mimeType: 'application/dxf' },
  fbx: { extension: 'fbx', mimeType: 'model/x-autodesk.filmbox' },
  ply: { extension: 'ply', mimeType: 'model/x-polygon' },
  skeleton: { extension: 'json', mimeType: 'application/json' },
  stl: { extension: 'stl', mimeType: 'application/sla' },
  obj: { extension: 'obj', mimeType: 'model/x-wavefront.obj' },
  gltf: { extension: 'gltf', mimeType: 'model/gltf+json' },
  glb: { extension: 'glb', mimeType: 'model/gltf-binary' },
  stp: { extension: 'stp', mimeType: 'application/step' },
};


class ModalEdge3DViewer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedClusterIdx: 0,
      scriptLoading: true,
      sceneLoading: false,
      modelLoading: false,
      widget: null,
      skeletonJsonData: null,
      widgetController: null,
      displayDimensions: false,
      dimensionsTrackingId: null,
      editModeEnabled: false,
      cameraSelectedLabel: null,
      currentDownloads: {}
    };
  }

  get sceneLoading() {
    return this.state.scriptLoading || this.state.sceneLoading;
  }

  getCameraSets = () => {
    return {
      front: {
        direction: window.Views?.Direction?.FrontToBack,
        baseImageLink: 'camera-front',
        label: 'front'
      },
      back: {
        direction: window.Views?.Direction?.BackToFront,
        baseImageLink: 'camera-back',
        label: 'back'
      },
      left: {
        direction: window.Views?.Direction?.LeftToRight,
        baseImageLink: 'camera-left',
        label: 'left'
      },
      right: {
        direction: window.Views?.Direction?.RightToLeft,
        baseImageLink: 'camera-right',
        label: 'right'
      },
      top: {
        direction: window.Views?.Direction?.TopToBottom,
        baseImageLink: 'camera-top',
        label: 'top'
      },
      bottom: {
        direction: window.Views?.Direction?.BottomToTop,
        baseImageLink: 'camera-bottom',
        label: 'bottom'
      },
    };
  }

  rotateView = (direction) => {
    this.state.widget
      .getController()
      .then((controller) => {
        const cameraSets = this.getCameraSets();
        const cameraDirection = cameraSets[direction].direction;
        controller.viewManager.viewSceneFromDirection(cameraDirection, true);
        window.scrollTo(0, 0);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  orbitsControlsChanged = (event) => {
    if (event.action && event.action !== 'rotate') {
      return;
    }

    const cameraSets = this.getCameraSets();

    let cameraPosition;
    if (event.target.camera) {
      cameraPosition = event.target.camera.position;
    }else{
      cameraPosition = event.target.object.position;
    }

    const cameraVector = event.target.target
      .clone()
      .sub(cameraPosition)
      .normalize();

    let minAngle = 999999.9;

    let cameraSelected;

    for (const cameraKey in cameraSets) {
      const camera = cameraSets[cameraKey];
      const cameraAngle = (Math.acos(camera.direction.dot(cameraVector)) * 180.0) / Math.PI;

      if (cameraAngle < minAngle) {
        minAngle = cameraAngle;
        cameraSelected = camera;
      }
    }


    if (this.state.cameraSelectedLabel !== cameraSelected.label) {
      this.updateStateIfMounted({cameraSelectedLabel: cameraSelected.label});
    }
  }

  loadClusterModel = (clusterIdx) => {
    this.updateStateIfMounted({sceneLoading: true, modelLoading: true});
    get3DViewerElementsNewAPI(this.props.configuration.blocks()[clusterIdx]).then((response) => {
      const assembly = response.data.assemblies?.[0];
      get3DViewerAssemblyComposition(assembly).then(
        (response) => {
          const skeletonJsonData = response.data;
          const a = document.getElementById("sdk3d-3d-widget");
          this.updateStateIfMounted({widget: a, skeletonJsonData, modelLoading: false});
          a &&
            a.getController().then((controller) => {
              controller.config.modelsResourcesEndpoint = modelsResourcesEndpoint;
              controller.config.referencePointsEndpoint = referencePointsEndpoint;
              controller.config.baseHeaders.Authorization = authBearerToken;
              this.updateStateIfMounted({widgetController: controller});
              controller.loadScene({
                skeleton: skeletonJsonData,
              })
                .then(() => {
                  const { viewManager } = controller;
                  const { modelManager } = controller;

                  const commercialReferenceToObjects = modelManager
                    ? modelManager.commercialReferenceToObjects
                    : [];

                  for (const refKey in commercialReferenceToObjects) {
                    if (refKey === "03001") {
                      modelManager.removeObject(
                        commercialReferenceToObjects[refKey][0]
                      );
                      viewManager.forceRender();
                      viewManager.focusOnAll();
                    }
                  }

                  if (this.state.displayDimensions) {
                    const dimensionsTrackingId = controller.overlayManager.trackDimensions(
                      [controller.scene],
                    );
                    this.updateStateIfMounted({dimensionsTrackingId});
                  }

                  controller.viewManager.controls.addEventListener(
                    'change',
                    this.orbitsControlsChanged,
                  );
                })
                .catch((a) => {
                  console.error("Loading scene error : ", a);
                }).finally(() => {
                  this.updateStateIfMounted({sceneLoading: false});
                });
            });
        }
      ).catch(() => {
        this.updateStateIfMounted({sceneLoading: false});
      });
    }).catch(() => {
      this.updateStateIfMounted({sceneLoading: false});
    });
  };

  componentDidMount() {
    this._isMounted = true;
    this.updateStateIfMounted({ scriptLoading: true });

    if (document.getElementById(VIEWER_SCRIPT_ID) == null) {
      const s = document.createElement("script");
      s.type = "module";
      s.id = VIEWER_SCRIPT_ID;
      s.src =
        "https://visuals.se.com/scripts/3d-widget/se-3d-widget.esm.js";
      document.head.append(s);
      s.onload = () => {
        this.loadClusterModel(0);
        this.updateStateIfMounted({ scriptLoading: false });
      };
    } else {
      this.loadClusterModel(0);
      this.updateStateIfMounted({ scriptLoading: false });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  updateStateIfMounted = (newState) => {
    if (this._isMounted) {
      this.setState(newState);
    }
  };

  rotateView = (direction) => {
    this.state.widget
      .getController()
      .then((controller) => {
        const cameraSets = this.getCameraSets();
        const cameraDirection = cameraSets[direction].direction;
        controller.viewManager.viewSceneFromDirection(cameraDirection, true);
        window.scrollTo(0, 0);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  buildSelectedViewUrl = () => {
    const cameraSets = this.getCameraSets();

    if (this.state.cameraSelectedLabel) {
      return `${front_base_url}/images/3d-viewer/${cameraSets[this.state.cameraSelectedLabel].baseImageLink}-selected.png`;
    }

    return `${front_base_url}/images/3d-viewer/camera-free.png`;
  }

  buildViewImageUrl = (cameraSet) => {
    const cameraSets = this.getCameraSets();
    return `${front_base_url}/images/3d-viewer/${cameraSets[cameraSet].baseImageLink}-small${this.state.cameraSelectedLabel === cameraSet ? '-selected' : ''}.png`;
  }

  buildPossibleView = (direction) => {
    return (
      <PossibleView $disabled={this.sceneLoading} onClick={() => {
        this.rotateView(direction);
      }} src={this.buildViewImageUrl(direction)}></PossibleView>
    );
  }

  downloadDocument = (documentType) => {
    const downloadName = `${this.props.configuration.name}-${this.props.configuration.id}-cluster-${(this.state.selectedClusterIdx + 1)}-3d-composition-${documentType}`;

    const downloadFormatInformation = DOWNLOAD_FORMATS[documentType];

    function promptDownload(response, documentTypeExtension) {
      const fileSrc = new Blob([response.data], { type: 'plain/text' });
      const extension = response.headers['content-type'] === 'application/zip'
        ? 'zip'
        : documentTypeExtension;
      const fileName = `${downloadName}.${extension}`;
      if (window.navigator && window.navigator.msSaveBlob) {
        window.navigator.msSaveBlob(fileSrc, fileName);
      } else {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        a.href = window.URL.createObjectURL(fileSrc);
        a.download = fileName;
        a.click();
        document.body.removeChild(a);
      }
    }

    if (documentType === 'skeleton') {
      const skeletonJSON = JSON.stringify(this.state.skeletonJsonData, null, 2);
      promptDownload(
        { data: skeletonJSON, headers: {} },
        downloadFormatInformation.extension,
      );
      log3DViewerExportConfiguration(this.props.configuration, downloadFormatInformation.extension, `.${downloadFormatInformation.extension}`);
    } else {
      const skeletonHeaders = {
        'Content-Type' : 'application/json',
        Accept: downloadFormatInformation.mimeType,
        Authorization: `${authBearerToken}`,
      };

      this.updateStateIfMounted({
        currentDownloads: {
          ...this.state.currentDownloads,
          [documentType]: true
        }
      });

      API_INSTANCE_NO_CREDENTIALS
        .post(`${skeletonEndpoint}`, this.state.skeletonJsonData, {
          responseType: 'arraybuffer',
          headers: skeletonHeaders,
          mode: 'no-cors',
          params: {
            databaseMode: 'design/obj/marked/low-poly',
          },
        })
        .then((success) => {
          promptDownload(success, downloadFormatInformation.extension);
          log3DViewerExportConfiguration(this.props.configuration, downloadFormatInformation.extension, `.${downloadFormatInformation.extension}`);
        })
        .catch((error) => {
          log3DViewerExportErrorConfiguration(this.props.configuration, downloadFormatInformation.extension);
          if (error && error.response) {
            // Response is an array buffer, convert it into a String to display it.
            console.log(
              String.fromCharCode.apply(
                null,
                new Uint8Array(error.response.data),
              ),
            );
          } else {
            console.log(error);
          }
        })
        .finally(() => {
          const currentDownloads = {...this.state.currentDownloads};
          delete currentDownloads[documentType];
          this.updateStateIfMounted({currentDownloads});
        });
    }
  }

  render = () => {
    const clusters = this.props.configuration.blocks();
    const nbDownloadsInProgress = Object.keys(this.state.currentDownloads).length;
    return (
      <Container>
        <Header>
          <TitleWrapper>
            <Title>
              {I10n.get("screen.config_editor.3d_viewer_modal.title")}
            </Title>
          </TitleWrapper>
          <CloseIcon
            onClick={(e) => {
              e.preventDefault();
              this.props.closeModal();
            }}
          >
            <MsxIcons name="close"></MsxIcons>
          </CloseIcon>
        </Header>
        <Content>
          {clusters.length > 1 && <ClusterWrapper>
            {clusters.map((cluster, idx) => {
              return (
                <ClusterItem
                  key={idx}
                  $selected={idx === this.state.selectedClusterIdx}
                  $disabled={this.sceneLoading}
                  onClick={() => {
                    if (idx !== this.state.selectedClusterIdx) {
                      this.loadClusterModel(idx);
                      this.updateStateIfMounted({ selectedClusterIdx: idx });
                    }
                  }}
                >
                  {`Cluster ${idx + 1}`}
                </ClusterItem>
              );
            })}
          </ClusterWrapper>}
          <ViewerWrapper>
            {this.state.modelLoading && <LoaderContainer>
              <LoadingRing>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </LoadingRing>
            </LoaderContainer>}
            {this.state.scriptLoading ? (
              "loading"
            ) : (
              <se-3d-widget
                id="sdk3d-3d-widget"
                class="hydrated graphical-view-positioner"
              ></se-3d-widget>
            )}
          </ViewerWrapper>
          <ActionSectionsWrapper>
            <ActionSection>
              <ActionSectionHeader>
                <HeaderTitle>{I10n.get('screen.config_editor.3d_viewer_modal.views')}</HeaderTitle>
              </ActionSectionHeader>
              <SectionContent>
                <SelectedViewWrapper>
                  <SelectedViewImage src={this.buildSelectedViewUrl()}></SelectedViewImage>
                </SelectedViewWrapper>
                <SelectedViewSeparator></SelectedViewSeparator>
                <PossibleViewsWrapper>
                  {this.buildPossibleView('front')}
                  {this.buildPossibleView('back')}
                  {this.buildPossibleView('left')}
                  {this.buildPossibleView('right')}
                  {this.buildPossibleView('top')}
                  {this.buildPossibleView('bottom')}
                </PossibleViewsWrapper>
              </SectionContent>
            </ActionSection>
            {this.props.showExports && <ActionSection>
              <ActionSectionHeader>
                <HeaderTitle>{I10n.get('screen.config_editor.3d_viewer_modal.download')}</HeaderTitle>
              </ActionSectionHeader>
              <SectionContent>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['stp'] === true || nbDownloadsInProgress > 0} onClick={() => this.downloadDocument('stp')}>
                  <div>Step 3D CAD (.stp)</div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['collada'] === true || nbDownloadsInProgress > 0} id="download-collada" onClick={() => this.downloadDocument('collada')}>
                    <div>
                      <div>Collada(.dae)</div>
                    </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['dxf'] === true || nbDownloadsInProgress > 0} id="download-dxf" onClick={() => this.downloadDocument('dxf')}>
                  <div>
                    <div>Drawing eXchange Format (.dxf)</div>
                  </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['fbx'] === true || nbDownloadsInProgress > 0} id="download-fbx" onClick={() => this.downloadDocument('fbx')}>
                  <div>
                    <div>FilmBoX (.fbx)</div>
                  </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['ply'] === true || nbDownloadsInProgress > 0} id="download-ply" onClick={() => this.downloadDocument('ply')}>
                  <div>
                    <div>PoLYgon (.ply)</div>
                  </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['skeleton'] === true || nbDownloadsInProgress > 0} id="download-skeleton" onClick={() => this.downloadDocument('skeleton')}>
                  <div>
                    <div>Skeleton (.json)</div>
                  </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['stl'] === true || nbDownloadsInProgress > 0} id="download-stl" onClick={() => this.downloadDocument('stl')}>
                  <div>
                    <div>STereoLithography (.stl)</div>
                  </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['obj'] === true || nbDownloadsInProgress > 0} id="download-obj" onClick={() => this.downloadDocument('obj')}>
                  <div>
                    <div>Wavefront (.obj)</div>
                  </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['gltf'] === true || nbDownloadsInProgress > 0} id="download-gltf" onClick={() => this.downloadDocument('gltf')}>
                  <div>
                    <div>glTF JSON (.gltf)</div>
                  </div>
                </Link>
                <Link $disabled={this.sceneLoading || this.state.currentDownloads['glb'] === true || nbDownloadsInProgress > 0} id="download-glb" onClick={() => this.downloadDocument('glb')}>
                  <div>
                    <div>Binary glTF (.glb)</div>
                  </div>
                </Link>
              </SectionContent>
            </ActionSection>}
            <ActionSection>
              <ActionSectionHeader>
                <HeaderTitle>{I10n.get('screen.config_editor.3d_viewer_modal.features')}</HeaderTitle>
              </ActionSectionHeader>
              <SectionContent>
                <CheckboxWrapper>
                  <Checkbox
                    checked={this.state.displayDimensions}
                    disabled={this.sceneLoading}
                    label={I10n.get('screen.config_editor.3d_viewer_modal.display_dimensions')}
                    onChange={(event) => {
                      event.preventDefault();
                      const newState = !this.state.displayDimensions;
                      if (this.state.widgetController) {
                        let dimensionsTrackingId = null;
                        if (newState) {
                          dimensionsTrackingId = this.state.widgetController.overlayManager.trackDimensions(
                            [this.state.widgetController.scene],
                          );
                        } else {
                          this.state.widgetController.overlayManager.cancelDimensionsTracking(
                            this.state.dimensionsTrackingId,
                          );
                        }
                        this.updateStateIfMounted({displayDimensions: newState, dimensionsTrackingId});
                      }
                    }}
                  />
                </CheckboxWrapper>
              </SectionContent>
            </ActionSection>
          </ActionSectionsWrapper>
        </Content>
      </Container>
    );
  };
}

export default ModalEdge3DViewer;
